import { Shield, CheckCircle, FileCheck, Droplets } from 'lucide-react';

const Quality = () => {
  return (
    <div className="font-inter" data-testid="quality-page">
      {/* Header */}
      <section className="bg-[#2B2B2B] text-white py-16" data-testid="quality-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl mb-4">
            Quality & Compliance
          </h1>
          <p className="text-xl text-gray-300">
            Meeting international standards for export excellence
          </p>
          <div className="w-24 h-1 bg-[#B11217] mt-4"></div>
        </div>
      </section>

      {/* ISPM-15 Certification */}
      <section className="py-16 bg-white" data-testid="ispm15-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-20 h-20 bg-[#B11217] rounded-full flex items-center justify-center mb-6">
                <Shield size={40} className="text-white" />
              </div>
              <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-6">
                ISPM-15 Heat Treatment
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                All our wooden pallets and packaging materials undergo ISPM-15 compliant heat treatment to eliminate pests and prevent the spread of invasive species across international borders.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Our certified heat treatment process involves heating wood to a core temperature of 56°C for at least 30 minutes, ensuring complete compliance with international phytosanitary measures.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Each product is clearly marked with the ISPM-15 stamp, certifying its compliance and making customs clearance smooth and hassle-free.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1647427060118-4911c9821b82"
                alt="Manufacturing Process"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quality Standards Grid */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="quality-standards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-12 text-center">
            Our Quality Standards
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg text-center" data-testid="standard-ispm15">
              <div className="w-16 h-16 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield size={32} className="text-white" />
              </div>
              <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-3">ISPM-15 Certified</h3>
              <p className="text-gray-600 text-sm">
                International compliance for wood packaging material
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg text-center" data-testid="standard-export">
              <div className="w-16 h-16 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-3">Export Ready</h3>
              <p className="text-gray-600 text-sm">
                Meets all international export requirements and standards
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg text-center" data-testid="standard-moisture">
              <div className="w-16 h-16 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <Droplets size={32} className="text-white" />
              </div>
              <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-3">Moisture Control</h3>
              <p className="text-gray-600 text-sm">
                Controlled drying process ensures optimal moisture levels
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg text-center" data-testid="standard-inspection">
              <div className="w-16 h-16 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <FileCheck size={32} className="text-white" />
              </div>
              <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-3">Quality Inspection</h3>
              <p className="text-gray-600 text-sm">
                Rigorous multi-stage quality control and inspection process
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Export Compliance */}
      <section className="py-16 bg-white" data-testid="export-compliance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-8 text-center">
            Export Compliance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#F2F2F2] p-8 border-l-4 border-[#B11217]">
              <h3 className="font-poppins font-semibold text-xl text-[#2B2B2B] mb-4">International Standards</h3>
              <p className="text-gray-600 leading-relaxed">
                Our products comply with ISPM-15 (International Standards for Phytosanitary Measures No. 15), ensuring acceptance in all major export markets including USA, Europe, Australia, and Asia.
              </p>
            </div>
            <div className="bg-[#F2F2F2] p-8 border-l-4 border-[#B11217]">
              <h3 className="font-poppins font-semibold text-xl text-[#2B2B2B] mb-4">Documentation</h3>
              <p className="text-gray-600 leading-relaxed">
                We provide complete documentation including heat treatment certificates, fumigation reports, and phytosanitary certificates to facilitate smooth customs clearance.
              </p>
            </div>
            <div className="bg-[#F2F2F2] p-8 border-l-4 border-[#B11217]">
              <h3 className="font-poppins font-semibold text-xl text-[#2B2B2B] mb-4">Traceability</h3>
              <p className="text-gray-600 leading-relaxed">
                Every pallet is marked with a unique identification code, ensuring complete traceability from production to delivery for quality assurance and compliance.
              </p>
            </div>
            <div className="bg-[#F2F2F2] p-8 border-l-4 border-[#B11217]">
              <h3 className="font-poppins font-semibold text-xl text-[#2B2B2B] mb-4">Regular Audits</h3>
              <p className="text-gray-600 leading-relaxed">
                Our facilities undergo regular third-party audits to ensure continued compliance with international standards and best practices in wood treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Inspection Process */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="inspection-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-12 text-center">
            Quality Inspection Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 text-center">
              <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-poppins font-bold text-xl">1</span>
              </div>
              <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-3">Raw Material Inspection</h3>
              <p className="text-gray-600 text-sm">
                Thorough inspection of incoming wood for quality, moisture content, and defects before production begins.
              </p>
            </div>
            <div className="bg-white p-6 text-center">
              <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-poppins font-bold text-xl">2</span>
              </div>
              <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-3">In-Process Monitoring</h3>
              <p className="text-gray-600 text-sm">
                Continuous quality checks during manufacturing to ensure dimensional accuracy and structural integrity.
              </p>
            </div>
            <div className="bg-white p-6 text-center">
              <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-poppins font-bold text-xl">3</span>
              </div>
              <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-3">Final Inspection</h3>
              <p className="text-gray-600 text-sm">
                Comprehensive final inspection and testing before dispatch to ensure products meet all specifications.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quality;